import React, { Ref } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useUniqueId } from '@/utils/hooks';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { IconProp, RotateProp } from '@fortawesome/fontawesome-svg-core';
import { TranslatableText } from '@/types';
import theme from './Button.module.scss';
import { Tooltip } from '@/components/Tooltip';

export type ButtonColor = 'primary' | 'secondary' | 'tertiary' | 'success' | 'danger' | 'warning' | 'info' | 'link' | 'text';

export type ButtonSize = 'xsmall' | 'small' | 'normal' | 'large' | 'xlarge';

export interface ButtonElementProps {
    color?: ButtonColor;
    icon?: IconProp;
    iconRotation?: RotateProp;
    iconPosition?: 'left' | 'right';
    disabled?: boolean;
    loading?: boolean;
    block?: boolean;
    tooltip?: TranslatableText | null;
    spanClassName?: string;
    size?: ButtonSize;
    className?: string;
    rounded?: boolean;
    outline?: boolean;
    ref?: Ref<HTMLButtonElement>;
}

type Props = ButtonElementProps & { tag: keyof React.ReactHTML | React.ComponentType<any>; [key: string]: any };
export const ButtonElement = React.forwardRef<any, Props>(
    (
        {
            children,
            color,
            className,
            icon,
            iconPosition = 'right',
            iconRotation,
            disabled,
            loading,
            block = false,
            tooltip = null,
            spanClassName,
            size = 'medium',
            rounded = false,
            tag,
            outline = false,
            ...rest
        },
        ref
    ) => {
        // const themeClassName = theme ? `button button-${theme}` : '';
        const uniqueId = useUniqueId();
        const classNames: string[] = [theme[size]];

        if (color) {
            classNames.push(theme.button, theme[color]);
        }

        if (outline) {
            classNames.push(theme.outline);
        }

        if (block) {
            classNames.push(theme.block);
        }

        if (loading) {
            classNames.push(theme.loading);
        }

        if (className) {
            classNames.push(className);
        }

        const iconElement = icon ? (
            <FontAwesomeIcon fixedWidth icon={icon} className="icon" key="2" rotation={iconRotation} />
        ) : null;

        const props = {
            id: uniqueId,
            className: classNames.join(' '),
            disabled: disabled || loading,
            ref: ref,
            ...rest,
        };

        const buttonElement = React.createElement(tag, props, [
            // loading && <FontAwesomeIcon className="icon" icon={faSpinner} spin key="1" />,
            iconPosition === 'left' && iconElement,
            children &&
                (loading ? (
                    <FontAwesomeIcon className="icon" icon={faSpinner} spin key="1" size="lg" />
                ) : (
                    <span className={spanClassName} key="3">
                        {children}
                    </span>
                )),
            iconPosition === 'right' && iconElement,
        ]);

        if (tooltip) {
            return (
                <Tooltip content={tooltip}>
                    <span>{buttonElement}</span>
                </Tooltip>
            );
        }

        return buttonElement;
    }
);